import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { getProductByGtin } from "@/data/products";
import { ArrowLeft, Download, QrCode } from "lucide-react";

const LOGO_URL =
  "https://balance8.co.uk/cdn/shop/files/Logo_02_b2cedfc7-a338-4500-a9d9-bd23002b1cb6.png?v=1728339609";

export function QRPage() {
  const { gtin } = useParams<{ gtin: string }>();
  const product = gtin ? getProductByGtin(gtin) : undefined;
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [productUrl, setProductUrl] = useState<string>("");

  useEffect(() => {
    if (!product || !gtin) return;
    const url = `${window.location.origin}/${gtin}`;
    setProductUrl(url);
    QRCode.toDataURL(url, {
      width: 400,
      margin: 2,
      color: { dark: "#020912", light: "#ffffff" },
    })
      .then(setQrDataUrl)
      .catch(console.error);
  }, [product, gtin]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#fafafa] font-[Poppins] flex flex-col">
        <header className="pt-10 pb-4 flex justify-center px-6">
          <img src={LOGO_URL} alt="Balance8" className="h-12 w-auto" />
        </header>

        <main className="flex-1 flex flex-col items-center justify-center px-6">
          <h1 className="text-2xl font-light text-[#020912] mb-2">
            Product Not Found
          </h1>
          <p className="text-sm text-gray-400 mb-8">
            This QR code does not match any product.
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-xl">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Products
            </Button>
          </Link>
        </main>

        <footer className="py-6 text-center border-t border-gray-100">
          <p className="text-[10px] text-gray-300 tracking-widest uppercase">
            Redefining Complete Wellness
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa] font-[Poppins] flex flex-col">
      <header className="pt-10 pb-4 flex justify-center px-6">
        <img src={LOGO_URL} alt="Balance8" className="h-12 w-auto" />
      </header>

      <main className="flex-1 flex flex-col items-center px-6 pt-6 pb-12 max-w-lg mx-auto w-full">
        <div className="flex items-center gap-2 mb-2">
          <QrCode className="h-4 w-4 text-gray-400" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">
            QR Code
          </p>
        </div>

        <h1 className="text-xl font-light text-[#020912] text-center mb-1">
          {product.name}
        </h1>
        <p className="text-xs text-gray-400 text-center mb-8">
          Scan to visit the product page
        </p>

        {/* QR Code */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt={`QR code for ${product.name}`}
              className="w-64 h-64"
            />
          ) : (
            <div className="w-64 h-64 bg-gray-100 rounded-lg animate-pulse" />
          )}
        </div>

        {/* URL */}
        <p className="text-[10px] text-gray-400 text-center mb-8 break-all font-mono max-w-xs">
          {productUrl}
        </p>

        {/* Download button */}
        {qrDataUrl && (
          <a
            href={qrDataUrl}
            download={`balance8-${gtin}-qr.png`}
            className="w-full max-w-sm"
          >
            <Button className="w-full h-12 text-sm bg-[#020912] hover:bg-[#0a1a2e] text-white font-medium shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all rounded-xl">
              <Download className="h-4 w-4 mr-2" />
              Download QR Code
            </Button>
          </a>
        )}

        <div className="mt-6">
          <Link
            to={`/${gtin}`}
            className="text-xs text-gray-400 hover:text-[#020912] transition-colors"
          >
            View product page →
          </Link>
        </div>
      </main>

      <footer className="py-6 text-center border-t border-gray-100">
        <p className="text-[10px] text-gray-300 tracking-widest uppercase">
          Redefining Complete Wellness
        </p>
      </footer>
    </div>
  );
}
